import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../../store/store';
import { useCallback, useEffect, useMemo } from 'react';
import {
  FUND_SEARCH_CATEGORY_FIELDS,
  PORTFOLIO_SEARCH_CATEGORY_FIELDS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_SEARCH_CATEGORY_FIELDS,
  PROFILER_SEARCH_FIELDS,
  SEARCH_FIELDS,
  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA
} from '../../../../utils/constants/fields';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import MultiSelect from '../../../../components/inputs/MultiSelect';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
  PROFILER_SEARCH_CATEGORIES,
  SEARCH_CATEGORIES
} from '../../../../utils/constants/searchCategories';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import ProfilerCompanyNameSearch from '../../../Home/components/agent/ProfilerCompanyNameSearch';
import IndividualsNameSearch from '../../../Home/components/profiler/individuals-name/IndividualsNameSearch';
import BulkAdjustPanel from './BulkAdjustPanel';
import {
  isProfilerAdvancedCriteriaDisabled,
  isProfilerAdvancedCriteriaSituationDisabled,
  isProfilerAdvancedCriteriaOwnershipDisabled
} from '../../../../utils/constants/variables';

const DesktopFilters = observer(
  ({
    mainState,
    bulkAdjust,
    fundsInvestmentCriteriaFields,
    portfoliosInvestmentCriteriaFields,
    profilerCompanyCriteriaFields,
    profilerIndustryCodesFields
  }) => {
    const { utilsStore, profilerAccessStore } = useStore();
    const localState = useLocalObservable(() => ({
      get isFundSearch() {
        return (
          mainState.fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.NAME][0] ===
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds
        );
      },
      get isFundInvestmentCriteriaSearch() {
        return (
          localState.isFundSearch &&
          mainState.fundsSearchCategoryFields[
            FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
          ][0] === UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria']
        );
      },
      get isPortfolioSearch() {
        return (
          mainState.fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.NAME][0] ===
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.']
        );
      },
      get isPortfolioInvestmentCriteriaSearch() {
        return (
          localState.isPortfolioSearch &&
          mainState.portfoliosSearchCategoryFields[
            PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
          ][0]
        );
      },
      get isProfilerSearch() {
        return (
          mainState.fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.NAME][0] ===
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
        );
      },
      get isProfilerCompanyCriteriaSearch() {
        return (
          localState.isProfilerSearch &&
          mainState.profilerSearchCategoryFields[
            PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
          ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics']
        );
      },
      get isProfilerIndustryCodesSearch() {
        return (
          localState.isProfilerSearch &&
          mainState.profilerSearchCategoryFields[
            PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
          ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']
        );
      },
      get isProfilerCompanyNameSearch() {
        return (
          localState.isProfilerSearch &&
          mainState.profilerSearchCategoryFields[
            PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME
          ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Name']
        );
      },
      get isProfilerBulkSearch() {
        return (
          localState.isProfilerSearch &&
          mainState.profilerSearchCategoryFields[
            PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME
          ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Bulk']
        );
      },
      get isIndividualsSearch() {
        return (
          mainState.fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.NAME][0] ===
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals
        );
      }
    }));

    useEffect(() => {
      if (utilsStore.windowWidth < 834 && mainState.isDesktopFiltersToggled) {
        mainState.toggleDesktopFilters();
      }
    }, [mainState, utilsStore.windowWidth]);

    useEffect(() => {
      return () => {
        if (mainState.profilerIndustriesSearchTreeToggled) {
          mainState.toggleProfilerIndustriesSearchTree();
        }
      };
    }, [mainState]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        const isInsideFilters = event.target.closest('.desktop-filters-wrap');
        const isAdjustButton = event.target.closest('.adjust-search-button');
        const isClearProfilerNameSearch = event.target.closest('#profiler-name-search-clear-icon');
        // Keep the panel open while a bulk upload/review is active — the inline BulkUploadReview
        // would otherwise be discarded by a stray outside click.
        const isBulkFlowActive = Boolean(bulkAdjust?.bulk?.status && bulkAdjust.bulk.status !== 'idle');

        if (!isInsideFilters && !isAdjustButton && !isClearProfilerNameSearch && !isBulkFlowActive) {
          if (mainState.isDesktopFiltersToggled) {
            mainState.toggleDesktopFilters();
          }
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [mainState, bulkAdjust]);

    const searchTreeToggled = mainState.profilerIndustriesSearchTreeToggled;
    const toggleSearchTree = mainState.toggleProfilerIndustriesSearchTree;
    const isTyping = mainState.profilerIndustriesSearchIsTyping;
    const searchKey = mainState.profilerIndustriesSearchSearchKey;
    const setSearchKey = mainState.setprofilerIndustriesSearchSearchKey;
    const industryCodesFlat = mainState.industryCodesFlat;
    const industryCodesRaw = mainState.industryCodesRaw;
    const toggleIndustryCode = mainState.toggleIndustryCode;
    const selectIndustryCode = mainState.selectIndustryCode;
    const toggleAll = mainState.toggleAllIndustryCodes;
    const untoggleAll = mainState.untoggleAllIndustryCodes;
    const clearAll = mainState.clearAllIndustryCodes;

    const selectedIndustryCodes = industryCodesFlat.filter((code) => code.selected);

    const flatMap = useMemo(() => {
      const map = new Map();
      industryCodesFlat.forEach((item) => {
        map.set(item.value, item);
      });
      return map;
    }, [industryCodesFlat]);

    const hasSelectedChildren = useCallback(
      (node) => {
        const currentNode = flatMap.get(node.value);
        if (!currentNode) return false;

        if (currentNode.children?.some((child) => flatMap.get(child.value)?.selected)) {
          return true;
        }

        return (
          currentNode.children?.some((child) => {
            const childNode = flatMap.get(child.value);
            return childNode ? hasSelectedChildren(childNode) : false;
          }) || false
        );
      },
      [flatMap]
    );

    const highlightMatches = useCallback((name, searchKey) => {
      if (!searchKey.trim()) return name;

      const lowerName = name.toLowerCase();
      const lowerSearch = searchKey.toLowerCase();
      const result = [];
      let lastIndex = 0;
      let index = lowerName.indexOf(lowerSearch);

      while (index !== -1) {
        if (index > lastIndex) {
          result.push(name.substring(lastIndex, index));
        }

        result.push(
          <span key={index} className="highlight">
            {name.substring(index, index + searchKey.length)}
          </span>
        );

        lastIndex = index + searchKey.length;
        index = lowerName.indexOf(lowerSearch, lastIndex);
      }

      if (lastIndex < name.length) {
        result.push(name.substring(lastIndex));
      }

      return result;
    }, []);

    const renderIndustryCode = useCallback(
      (code, level) => {
        const fromFlat = flatMap.get(code.value);
        const hasParent = !!fromFlat?.parent;
        const hasChildren = !!code.children?.length;
        const isToggled = !!fromFlat?.toggled;
        const isSelected = !!fromFlat?.selected;
        const showInnerSelectionsDot =
          !isToggled && !!fromFlat?.children?.length && hasSelectedChildren(fromFlat);

        return (
          <div
            className="industry-code-item-wrap"
            key={code.value}
            style={hasParent ? { marginLeft: '22px' } : {}}
          >
            <div
              className={`industry-code-item noselect ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                if (hasChildren) {
                  toggleIndustryCode(code);
                } else {
                  selectIndustryCode(code);
                }
              }}
            >
              {hasChildren && (
                <IconButton
                  icon={
                    isToggled ? ICON_BUTTON_ICONS.CHEVRON_DOWN : ICON_BUTTON_ICONS.CHEVRON_RIGHT
                  }
                  withFill={false}
                  withBorder={false}
                  wrapClassName="industry-code-item-toggle"
                />
              )}
              {!hasChildren && <div style={{ width: '18px', height: '18px', flexShrink: 0 }} />}
              <IconButton
                icon={
                  isSelected
                    ? ICON_BUTTON_ICONS.CHECK_SQUARE_FILL
                    : showInnerSelectionsDot
                      ? ICON_BUTTON_ICONS.CHECK_SQUARE
                      : ICON_BUTTON_ICONS.SQUARE
                }
                type={ICON_BUTTON_TYPES.BLUE}
                withFill={false}
                withBorder={false}
                wrapClassName={`industry-code-item-check ${
                  !isSelected && showInnerSelectionsDot ? 'light-blue' : ''
                }`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.nativeEvent?.preventDefault();
                  selectIndustryCode(code);
                }}
              />
              <div className="industry-code-item-name">
                {isTyping ? code.name : highlightMatches(code.name, searchKey)}
              </div>
            </div>
            {hasChildren &&
              isToggled &&
              code.children.map((child) => renderIndustryCode(child, level + 1))}
          </div>
        );
      },
      [
        flatMap,
        hasSelectedChildren,
        isTyping,
        searchKey,
        selectIndustryCode,
        toggleIndustryCode,
        highlightMatches
      ]
    );

    if (!mainState.isDesktopFiltersToggled || utilsStore.windowWidth < 834) {
      return null;
    }

    return (
      <div
        className={`desktop-filters-wrap ${
          localState.isFundInvestmentCriteriaSearch
            ? 'funds-investment'
            : localState.isPortfolioInvestmentCriteriaSearch
              ? 'portfolios-investment'
              : localState.isProfilerCompanyCriteriaSearch
                ? 'company-criteria'
                : localState.isProfilerIndustryCodesSearch
                  ? 'industry-codes'
                  : localState.isProfilerCompanyNameSearch
                    ? 'company-name'
                    : localState.isIndividualsSearch
                      ? 'company-name'
                      : ''
        }`}
      >
        <div className="fields-row">
          {fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.ID]}
          {localState.isFundSearch && (
            <MultiSelect
              field={FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
              value={
                mainState.fundsSearchCategoryFields[
                  FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
                ]
              }
              setFieldValue={mainState.setFundsSearchCategoryFieldValue}
              values={mainState.fundsCategories.filter(({ disabled }) => !disabled)}
              inputWrap={{ enable: true, className: 'col' }}
              // disabled={mainState.isLoading || mainState.isInitialLoading}
            />
          )}
          {localState.isPortfolioSearch && (
            <MultiSelect
              field={PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
              value={
                mainState.portfoliosSearchCategoryFields[
                  PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
                ]
              }
              setFieldValue={mainState.setPortfoliosSearchCategoryFieldValue}
              values={mainState.portfoliosCategories.filter(({ disabled }) => !disabled)}
              inputWrap={{ enable: true, className: 'col' }}
              // disabled={mainState.isLoading || mainState.isInitialLoading}
            />
          )}
          {localState.isProfilerSearch && (
            <MultiSelect
              field={PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
              value={
                mainState.profilerSearchCategoryFields[
                  PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
                ]
              }
              setFieldValue={mainState.setProfilerSearchCategoryFieldValue}
              values={mainState.profilerCategories.filter(({ disabled }) => !disabled)}
              inputWrap={{ enable: true, className: 'col' }}
              // disabled={mainState.isLoading || mainState.isInitialLoading}
            />
          )}
        </div>
        <div className="row-separator" />
        {localState.isProfilerSearch && profilerAccessStore.isProfilerDemo && (
          <div
            className={`profiler-demo-info-message${
              profilerAccessStore.demoSearchExceededLimit ? ' exceeded' : ''
            }`}
          >
            <div className="img-wrap">
              <img src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE} />
            </div>
            <div className="text">
              {profilerAccessStore.demoSearchExceededLimit ? (
                <>
                  You&apos;ve used all {profilerAccessStore.demoSearchMaxCount} demo searches -
                  upgrade to full access to continue exploring.
                </>
              ) : (
                <>
                  You have {profilerAccessStore.demoSearchRemainingCount} out of{' '}
                  {profilerAccessStore.demoSearchMaxCount} demo searches remaining - upgrade to full
                  access to unlock unlimited searches.
                </>
              )}
            </div>
          </div>
        )}
        {localState.isFundInvestmentCriteriaSearch && (
          <>
            <div className="fields-row">
              {fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.GENERAL][SEARCH_FIELDS.INDUSTRY.ID]}
              {
                fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.GENERAL][
                  SEARCH_FIELDS.HEADQUARTER.ID
                ]
              }
              {
                fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.GENERAL][
                  SEARCH_FIELDS.GEOGRAPHICAL_FOCUS.ID
                ]
              }
            </div>
            <div className="fields-row">
              {
                fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.TRANSACTION][
                  SEARCH_FIELDS.SITUATION.ID
                ]
              }
              {
                fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.TRANSACTION][
                  SEARCH_FIELDS.EQUITY_STAKE.ID
                ]
              }
            </div>
            <div className="fields-row">
              {
                fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.FINANCIALS][
                  SEARCH_FIELDS.REVENUE.ID
                ]
              }
              {fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.FINANCIALS][SEARCH_FIELDS.EBITDA.ID]}
              {
                fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.FINANCIALS][
                  SEARCH_FIELDS.EBITDA_MARGIN.ID
                ]
              }
            </div>
            <div className="fields-row">
              {
                fundsInvestmentCriteriaFields[SEARCH_CATEGORIES.VALUATION][
                  SEARCH_FIELDS.ENTERPRISE_MIN.ID + SEARCH_FIELDS.ENTERPRISE_MAX.ID
                ]
              }
              <div />
              <IconButton
                type={ICON_BUTTON_TYPES.BLUE}
                filled
                innerText="Apply criteria"
                disabled={mainState.isFundsInvestmentCriteriaSearchDisabled}
                wrapClassName="apply-criteria-button funds-investment-criteria"
                onClick={mainState.onSearch}
              />
            </div>
          </>
        )}
        {localState.isPortfolioInvestmentCriteriaSearch && (
          <>
            <div className="fields-row">
              {
                portfoliosInvestmentCriteriaFields[
                  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.INDUSTRY.ID
                ]
              }
              {
                portfoliosInvestmentCriteriaFields[
                  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.HEADQUARTER.ID
                ]
              }
              {
                portfoliosInvestmentCriteriaFields[
                  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.GEOGRAPHICAL_FOCUS.ID
                ]
              }
            </div>
            <div className="fields-row">
              {
                portfoliosInvestmentCriteriaFields[
                  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.REVENUE.ID
                ]
              }
              <div />
              <IconButton
                type={ICON_BUTTON_TYPES.BLUE}
                filled
                innerText="Apply criteria"
                disabled={mainState.isPortfoliosInvestmentCriteriaSearchDisabled}
                wrapClassName="apply-criteria-button portfolios-investment-criteria"
                onClick={mainState.onSearch}
              />
            </div>
          </>
        )}
        {localState.isProfilerCompanyCriteriaSearch && (
          <>
            <div className="fields-row">
              {profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.INDUSTRY.ID]}
              {profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.ID]}
              {profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.ID]}
            </div>
            <div className="fields-row">
              {
                profilerCompanyCriteriaFields[
                  PROFILER_SEARCH_FIELDS.REVENUE_MIN.ID + PROFILER_SEARCH_FIELDS.REVENUE_MAX.ID
                ]
              }
              <div className="col"></div>
              <div className="col"></div>
            </div>
            {isProfilerAdvancedCriteriaDisabled ? (
              <>
                <div className="fields-row">
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][
                      PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                        PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                    ]
                  }
                  <div className="col" />
                  <div className="col" />
                </div>
                <div className="fields-row">
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.HEADQUARTER.ID]
                  }
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                  }
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.DISTANCE.ID]
                  }
                </div>
              </>
            ) : (
              <>
                <div className="fields-row">
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.CAGR_MIN.ID + PROFILER_SEARCH_FIELDS.CAGR_MAX.ID]
                  }
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.CAGR_PERIOD.ID]
                  }
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][
                      PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.ID +
                        PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.ID
                    ]
                  }
                </div>
                <div className="fields-row">
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][
                      PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                        PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                    ]
                  }
                  {!isProfilerAdvancedCriteriaSituationDisabled && (
                    <>
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.SITUATION.ID]
                      }
                    </>
                  )}
                  {isProfilerAdvancedCriteriaSituationDisabled && <div className="col" />}
                  <div className="col" />
                </div>
                {!isProfilerAdvancedCriteriaOwnershipDisabled && (
                  <div className="fields-row">
                    {
                      profilerCompanyCriteriaFields[
                        PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                      ][PROFILER_SEARCH_FIELDS.LISTING_STATUS.ID]
                    }
                    {
                      profilerCompanyCriteriaFields[
                        PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                      ][PROFILER_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.ID]
                    }
                    {
                      profilerCompanyCriteriaFields[
                        PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                      ][PROFILER_SEARCH_FIELDS.OWNERSHIP_BASIS.ID]
                    }
                  </div>
                )}
                <div className="fields-row">
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.HEADQUARTER.ID]
                  }
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                  }
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_SEARCH_FIELDS.DISTANCE.ID]
                  }
                </div>
              </>
            )}
            <div className="fields-row">
              <div />
              <div />
              <IconButton
                type={ICON_BUTTON_TYPES.BLUE}
                filled
                innerText="Apply criteria"
                disabled={mainState.isProfilerCompanyCriteriaSearchDisabled}
                wrapClassName="apply-criteria-button"
                onClick={mainState.onSearch}
              />
            </div>
          </>
        )}
        {localState.isProfilerIndustryCodesSearch && (
          <>
            <div className="search-key-input-wrapper-industry-codes">
              <div className="search-key-label-industry-codes">
                Industries<font className="red-asterisk">*</font>
              </div>
              <div className="search-key-input-wrap-industry-codes">
                <input
                  className={`${selectedIndustryCodes.length ? 'placeholder-black' : ''} ${searchTreeToggled ? 'force-focused' : ''}`}
                  type="text"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  placeholder={
                    selectedIndustryCodes.length === 1
                      ? selectedIndustryCodes[0].name
                      : selectedIndustryCodes.length > 1
                        ? `${selectedIndustryCodes.length} selected`
                        : 'Select'
                  }
                  disabled={
                    // mainState.isLoading ||
                    // mainState.isInitialLoading ||
                    mainState.isProfilerIndustryCodesSearchFieldsDisabled
                  }
                  onFocus={() => {
                    if (!searchTreeToggled) {
                      toggleSearchTree();
                    }
                  }}
                />
                <IconButton
                  id="profiler-industry-codes-drop-icon"
                  icon={
                    searchTreeToggled
                      ? ICON_BUTTON_ICONS.CHEVRON_UP
                      : ICON_BUTTON_ICONS.CHEVRON_DOWN
                  }
                  withFill={false}
                  withBorder={false}
                  onClick={toggleSearchTree}
                  disabled={
                    // mainState.isLoading ||
                    // mainState.isInitialLoading ||
                    mainState.isProfilerIndustryCodesSearchFieldsDisabled
                  }
                />
                {searchTreeToggled && (
                  <div className="industry-codes-wrapper-outer">
                    <div className="industry-codes-actions-wrapper">
                      <IconButton
                        type={ICON_BUTTON_TYPES.BLUE}
                        withFill={false}
                        withBorder={false}
                        innerText="Toggle All"
                        onClick={toggleAll}
                      />
                      <IconButton
                        type={ICON_BUTTON_TYPES.BLUE}
                        withFill={false}
                        withBorder={false}
                        innerText="Untoggle All"
                        onClick={untoggleAll}
                      />
                      <IconButton
                        type={ICON_BUTTON_TYPES.BLUE}
                        withFill={false}
                        withBorder={false}
                        innerText="Clear All"
                        onClick={clearAll}
                      />
                    </div>
                    <div className="industry-codes-wrapper">
                      {industryCodesRaw.map((code) => renderIndustryCode(code, 1))}
                    </div>
                    <div className="fade-top" />
                    <div className="fade-bottom" />
                  </div>
                )}
              </div>
            </div>
            <div className="fields-row">
              {
                profilerIndustryCodesFields[
                  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.ID +
                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MAX.ID
                ]
              }
              <div className="col"></div>
              <div className="col"></div>
            </div>
            {isProfilerAdvancedCriteriaDisabled ? (
              <>
                <div className="fields-row">
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][
                      PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                    ]
                  }
                  <div className="col" />
                  <div className="col" />
                </div>
                <div className="fields-row">
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.ID]
                  }
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                  }
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE.ID]
                  }
                </div>
              </>
            ) : (
              <>
                <div className="fields-row">
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][
                      PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.ID +
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.ID
                    ]
                  }
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.ID]
                  }
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][
                      PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.ID +
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MAX.ID
                    ]
                  }
                </div>
                <div className="fields-row">
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][
                      PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                    ]
                  }
                  {!isProfilerAdvancedCriteriaSituationDisabled && (
                    <>
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.ID]
                      }
                    </>
                  )}
                  {isProfilerAdvancedCriteriaSituationDisabled && <div className="col" />}
                  <div className="col" />
                </div>
                {!isProfilerAdvancedCriteriaOwnershipDisabled && (
                  <div className="fields-row">
                    {
                      profilerIndustryCodesFields[
                        PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                      ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.LISTING_STATUS.ID]
                    }
                    {
                      profilerIndustryCodesFields[
                        PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                      ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.ID]
                    }
                    {
                      profilerIndustryCodesFields[
                        PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                      ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.OWNERSHIP_BASIS.ID]
                    }
                  </div>
                )}
                <div className="fields-row">
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.ID]
                  }
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                  }
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                    ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE.ID]
                  }
                </div>
              </>
            )}
            <div className="fields-row">
              <div />
              <div />
              <IconButton
                type={ICON_BUTTON_TYPES.BLUE}
                filled
                innerText="Apply criteria"
                disabled={mainState.isProfilerIndustryCodesSearchDisabled}
                wrapClassName="apply-criteria-button"
                onClick={mainState.onSearch}
              />
            </div>
          </>
        )}
        {localState.isProfilerCompanyNameSearch && (
          <ProfilerCompanyNameSearch
            isTyping={mainState.profilerNameSearchIsTyping}
            searchKey={mainState.profilerNameSearchSearchKey}
            setSearchKey={mainState.setprofilerNameSearchSearchKey}
            clearSearchKey={mainState.profilerNameSearchClearSearchKey}
            searchKeyResults={mainState.profilerNameSearchSearchKeyResults}
            isLoading={mainState.profilerNameSearchIsLoading}
            loadError={mainState.profilerNameSearchLoadError}
            isLoadingCompanyData={mainState.profilerNameSearchIsLoadingCompanyData}
            loadCompanyData={mainState.profilerNameSearchLoadCompanyData}
          />
        )}
        {localState.isProfilerBulkSearch && <BulkAdjustPanel bulkAdjust={bulkAdjust} />}
        {localState.isIndividualsSearch && (
          <IndividualsNameSearch inputId="search-results-individuals-searchKey" />
        )}
      </div>
    );
  }
);

export default DesktopFilters;
